import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { muscleName, loadMuscles } from "../store.js";
import { navigate } from "../core/router.js";
import { backLink } from "../components/common.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { barChart } from "../components/Chart.js";
import { spinner, errorState } from "../components/ui.js";
import { landmarksFor, setZone } from "../lib/landmarks.js";
import { relativeDay } from "../lib/format.js";

// Per-muscle drill-down behind the tappable recovery map: weekly hard sets vs the volume
// landmarks, the exercises that hit this muscle recently, and when it was last trained.
export default function MuscleDetail(params) {
  const root = h("div", { class: "screen" });
  load();

  async function load() {
    setChildren(root, backLink(), spinner());
    try {
      const [d] = await Promise.all([api.muscleDetail(params.id), loadMuscles()]);
      render(d);
    } catch (e) {
      setChildren(root, backLink(), errorState(e.message, load));
    }
  }

  function render(d) {
    const name = muscleName(d.muscle_id) || "Muscle";
    const title = name.charAt(0).toUpperCase() + name.slice(1);
    const lm = landmarksFor(name);

    // Zero-fill the weekly series over the window (server omits empty weeks). Weeks are
    // keyed by their Monday in local YYYY-MM-DD.
    const byWeek = new Map(d.series.map((s) => [s.week, s.sets]));
    const weeks = [];
    const monday = new Date();
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    for (let i = d.weeks - 1; i >= 0; i--) {
      const dt = new Date(monday);
      dt.setDate(dt.getDate() - i * 7);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      weeks.push({ x: key, y: byWeek.get(key) || 0 });
    }

    // Average over the last 4 whole weeks (incl. the current partial one — same rolling
    // window feel as the History volume card).
    const last4 = weeks.slice(-4);
    const perWeek = Math.round((last4.reduce((a, w) => a + w.y, 0) / last4.length) * 10) / 10;
    const zone = setZone(perWeek, lm);

    const fmtWk = (iso) => {
      const [y, m, day] = iso.split("-").map(Number);
      return new Date(y, m - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    };

    setChildren(root,
      backLink(),
      h("h1", { style: { margin: "4px 0 2px" } }, title),
      h("div", { class: "dim", style: { fontSize: ".84rem", marginBottom: "14px" } },
        d.last_trained_at ? `Last trained ${relativeDay(d.last_trained_at).toLowerCase()}` : "Never trained yet"),

      h("div", { class: "section-title" }, "Weekly hard sets"),
      h("div", { class: "card" },
        barChart(weeks, { format: (v) => `${Math.round(v)} sets`, xFormat: fmtWk }),
        h("div", { class: "hstack wrap", style: { marginTop: "10px", gap: "8px", alignItems: "center" } },
          h("span", { class: "badge", style: { background: "var(--surface-2)", color: zone.color } }, zone.label),
          h("span", { class: "dim", style: { fontSize: ".84rem" } }, `~${perWeek} hard sets/week over the last 4 weeks`)
        ),
        h("p", { class: "dim", style: { fontSize: ".8rem", marginTop: "8px" } }, zone.nudge),
        h("p", { class: "dim", style: { fontSize: ".74rem", marginTop: "6px" } },
          `Landmarks for ${name}: MEV ${lm.mev} · MAV ${lm.mav} · MRV ${lm.mrv} sets/week. Secondary-mover sets count half.`)
      ),

      h("div", { class: "section-title" }, `Exercises that hit it (last ${d.weeks} weeks)`),
      d.exercises.length
        ? h("div", { class: "card" }, ...d.exercises.map(exRow))
        : h("div", { class: "card" }, h("p", { class: "dim center" }, "No hard sets for this muscle in the window — time to change that.")),

      h("div", { class: "section-title" }, "Where it sits"),
      h("div", { class: "card", style: { padding: "10px" } }, MuscleMap({ primary: [name] }))
    );
  }

  function exRow(x) {
    const rel = relativeDay(x.last_at);
    const dateStr = rel.toLowerCase().includes("ago") || rel.toLowerCase().startsWith("last")
      ? rel.toLowerCase()
      : `last ${rel.toLowerCase()}`;
    return h("div", { class: "row-between card-tap", style: { padding: "9px 0" }, onClick: () => navigate(`/exercise/${x.exercise_id}`) },
      h("div", null,
        h("div", { style: { fontWeight: 700 } }, x.exercise_name),
        h("div", { class: "dim", style: { fontSize: ".82rem" } }, `${x.sets} hard set${x.sets === 1 ? "" : "s"} · ${dateStr}`)),
      h("span", { class: ["badge", "badge-muscle", x.role === "secondary" && "secondary"] }, x.role)
    );
  }

  return root;
}
